import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import type { WeekGrid } from "../../data/schedule-builder";
import { filterCoverageGrid, type CoverageFilter, type CoverageView } from "./coverage-view-state";
import "./coverage-views.css";

export function CoverageViews({ view, onViewChange, grid, staff, renderSite, recordsReady = true, stale = false }: {
  view: CoverageView;
  onViewChange: (view: CoverageView) => void;
  grid: WeekGrid;
  staff: ReactNode;
  renderSite: (grid: WeekGrid) => ReactNode;
  recordsReady?: boolean;
  stale?: boolean;
}) {
  const id = useId();
  const staffTab = useRef<HTMLButtonElement>(null);
  const siteTab = useRef<HTMLButtonElement>(null);
  const staffPanel = useRef<HTMLDivElement>(null);
  const sitePanel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const inactive = view === "staff" ? sitePanel.current : staffPanel.current;
    if (inactive?.contains(document.activeElement)) (view === "staff" ? staffTab : siteTab).current?.focus();
  }, [view]);
  const [site, setSite] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<CoverageFilter>("all");
  // Local filters stay out of URLs, telemetry and persistent storage. Keeping both panels
  // mounted preserves search and existing staff/drawer state on repeated view switches.
  const sites = Array.from(new Set(grid.posts.map(post => post.siteName))).sort((a, b) => a.localeCompare(b));
  const filtered = filterCoverageGrid(grid, site, search, filter);
  const hasFilters = site !== "" || search.trim() !== "" || filter !== "all";
  const choose = (next: CoverageView) => { onViewChange(next); (next === "staff" ? staffTab : siteTab).current?.focus(); };
  return <section className="coverage-views" aria-label="Schedule coverage views">
    <div className="coverage-views__tabs" role="tablist" aria-label="Coverage view" onKeyDown={event => {
      if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
      event.preventDefault();
      choose(event.key === "Home" ? "staff" : event.key === "End" ? "site" : view === "staff" ? "site" : "staff");
    }}>
      <button ref={staffTab} id={`${id}-staff-tab`} type="button" role="tab" aria-selected={view === "staff"}
        aria-controls={`${id}-staff-panel`} tabIndex={view === "staff" ? 0 : -1} onClick={() => onViewChange("staff")}>Staff</button>
      <button ref={siteTab} id={`${id}-site-tab`} type="button" role="tab" aria-selected={view === "site"}
        aria-controls={`${id}-site-panel`} tabIndex={view === "site" ? 0 : -1} onClick={() => onViewChange("site")}>Site coverage</button>
    </div>
    <div ref={staffPanel} id={`${id}-staff-panel`} role="tabpanel" aria-labelledby={`${id}-staff-tab`} hidden={view !== "staff"} tabIndex={0}>
      {staff}
    </div>
    <div ref={sitePanel} id={`${id}-site-panel`} role="tabpanel" aria-labelledby={`${id}-site-tab`} hidden={view !== "site"} tabIndex={0}>
      <div className="coverage-views__intro"><h2>Site coverage &amp; history</h2><p>Published, draft and cancelled shifts in the loaded service week. Empty cells do not confirm staff availability.</p></div>
      <div className="coverage-views__filters">
        <label>Site<select value={site} onChange={event => setSite(event.target.value)}>
          <option value="">All loaded sites</option>
          {site !== "" && !sites.includes(site) ? <option value={site}>{site} (not in loaded week)</option> : null}
          {sites.map(name => <option key={name} value={name}>{name}</option>)}
        </select></label>
        <label>Search sites, routes or credentials<input type="search" value={search} placeholder="Search loaded shifts" onChange={event => setSearch(event.target.value)} /></label>
        <label>Coverage<select value={filter} onChange={event => setFilter(event.target.value === "open" ? "open" : event.target.value === "at-risk" ? "at-risk" : "all")}>
          <option value="all">All coverage states</option><option value="open">Open</option><option value="at-risk">At risk</option>
        </select></label>
        <button type="button" className="builder__navbtn" disabled={!hasFilters} onClick={() => { setSite(""); setSearch(""); setFilter("all"); }}>Clear site filters</button>
      </div>
      {!recordsReady ? <p role="status">{stale ? "Coverage for the selected week is unavailable. Reload the week to review its records." : "Loading coverage for the selected week…"}</p> : <>
        <p className="coverage-views__count" role="status">{stale ? "Last loaded records: " : ""}Showing {filtered.summary.totalSlots} of {grid.summary.totalSlots} loaded slots · {filtered.summary.openSlots} open · {filtered.summary.atRiskSlots} at risk. Counts reflect loaded records and current site filters.</p>
        {hasFilters && filtered.posts.length === 0 ? <p role="status" className="surface__state">No loaded shifts match these filters. Clear site filters to review the loaded week.</p> : renderSite(filtered)}
      </>}
    </div>
  </section>;
}
